// 📄 pages/Admin/sections/TablaCatalogSection.tsx
import { useEffect, useState } from 'react';
import { Plus, RefreshCw, Table2 } from 'lucide-react';
import { TablaEstandar, getTablasEstandar, deleteTablaEstandar } from '../../../services/catalogoTablasService.ts';
import TablaCatalogGrid from '../../../components/tablas/TablaCatalogGrid.tsx';
import TablaFormModal from '../components/TablaFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

export default function TablaCatalogSection() {
  const [tablas, setTablas] = useState<TablaEstandar[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<TablaEstandar | null>(null);
  const [aEliminar, setAEliminar] = useState<TablaEstandar | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      setTablas(await getTablasEstandar());
    } catch (err) {
      console.error('Error cargando el catálogo de Tablas:', err);
      setError('No se pudo cargar el catálogo de Tablas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditando(null); setModalOpen(true); };
  const openEdit = (t: TablaEstandar) => { setEditando(t); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await deleteTablaEstandar(aEliminar.id);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar la tabla:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Catálogo de Tablas SAP</h1>
          <p className="text-sm text-gray-400 mt-1">Tablas estándar de referencia (ej. vía SE16N) con sus campos, visibles para TODOS los usuarios de la plataforma.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Nueva Tabla
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando catálogo...</p>
        </div>
      ) : tablas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Table2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay tablas registradas en el catálogo.</p>
        </div>
      ) : (
        <TablaCatalogGrid tablas={tablas} onEdit={openEdit} onDelete={setAEliminar} />
      )}

      {modalOpen && (
        <TablaFormModal tabla={editando} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta tabla del catálogo?"
          message={`"${aEliminar.nombreTecnico}" se eliminará del catálogo global. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
